interface SavePaymentMethodProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    enabled?: boolean; // Feature flag - controls visibility
    verificationMode?: boolean; // For $0 account verification
    onVerificationModeChange?: (enabled: boolean) => void;
}

export default function SavePaymentMethod({
    checked,
    onChange,
    enabled = false,
    verificationMode = false,
    onVerificationModeChange,
}: SavePaymentMethodProps) {
    // Don't render if feature is disabled
    if (!enabled) {
        return null;
    }

    return (
        <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
                {/* Custom Checkbox */}
                <div className="relative flex items-center justify-center">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                        className="w-5 h-5 border-2 border-gray-300 rounded-md checked:bg-gray-900 checked:border-gray-900 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors cursor-pointer"
                    />
                    {checked && (
                        <svg
                            className="absolute w-3 h-3 text-white pointer-events-none"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </div>

                {/* Label and Description */}
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700 transition-colors">
                            Save payment method for faster checkout
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Beta
                        </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                        <strong>By checking this box, you consent to:</strong>
                    </p>
                    <ul className="text-xs text-gray-600 mt-1 ml-4 space-y-0.5 list-disc">
                        <li>Securely store your payment information for future one-time purchases</li>
                        <li>Allow us to inform your card issuer that credentials are stored on file</li>
                        <li>Receive notifications about changes to stored credential terms</li>
                    </ul>
                    <p className="text-xs text-gray-600 mt-2">
                        You can view, update, or remove saved payment methods anytime from your account settings.
                    </p>
                </div>
            </label>

            {/* Account Verification Option */}
            {checked && onVerificationModeChange && (
                <div className="mt-3 ml-8">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={verificationMode}
                            onChange={(e) => onVerificationModeChange(e.target.checked)}
                            className="mt-0.5 w-4 h-4 border-2 border-gray-300 rounded checked:bg-gray-700 checked:border-gray-700 focus:ring-2 focus:ring-gray-700 focus:ring-offset-1 transition-colors cursor-pointer"
                        />
                        <div className="flex-1">
                            <span className="text-xs font-medium text-gray-800">
                                Save card only (no charge) - $0 account verification
                            </span>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Verify and save your card without placing an order. A $0 authorization will be processed
                                to confirm your card details.
                            </p>
                        </div>
                    </label>
                </div>
            )}

            {/* Security Note */}
            {checked && (
                <div className="mt-3 ml-8 p-3 bg-green-50 border border-green-200 rounded-lg animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-600 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <div className="flex-1">
                            <p className="text-xs text-green-800">
                                <strong>Secure Storage:</strong> Your payment method will be securely encrypted and
                                tokenized. We'll prompt for your CVV on future purchases for added security.
                            </p>
                            {verificationMode && (
                                <p className="text-xs text-green-800 mt-2">
                                    <strong>No Charge:</strong> Your card will be verified with a $0 authorization. No
                                    payment will be processed.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
